import React, { Component } from 'react';
import _ from 'lodash';
import axios from 'axios';
import {
  MegadraftEditor, editorStateFromRaw, editorStateToJSON,
} from 'megadraft';
import '../../node_modules/megadraft/dist/css/megadraft.css';
import '../styles/Editor.css';
import customImagePlugin from '../plugins/CustomImagePlugin/plugin';
import youtubePlugin from '../plugins/YoutubePlugin/plugin';

import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableContainer from '@material-ui/core/TableContainer';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

function getImpliedTitle(postJson) {
  try {
    const parsed = typeof postJson === 'string' ? JSON.parse(postJson) : postJson;
    if (parsed && parsed.blocks && parsed.blocks.length > 0) {
      const firstNonEmpty = parsed.blocks.find(b => b.text && b.text.trim().length > 0);
      return firstNonEmpty ? firstNonEmpty.text.trim() : 'Untitled Post';
    }
  } catch (e) {
    // Ignore error
  }
  return 'Untitled Post';
}

function getEditorStats(editorState) {
  try {
    const rawJson = editorStateToJSON(editorState);
    const parsed = JSON.parse(rawJson);
    let text = '';
    if (parsed && parsed.blocks) {
      text = parsed.blocks.map(b => b.text || '').join(' ');
    }
    const charCount = text.length;
    const words = text.trim() ? text.trim().split(/\s+/).filter(Boolean) : [];
    const wordCount = words.length;
    return { charCount, wordCount };
  } catch (e) {
    return { charCount: 0, wordCount: 0 };
  }
}

class Editor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editorState: editorStateFromRaw(null),
      activeSelectTopic: 'Jazz',
      entries: [],
      editingPostId: null,
      editingPostTitle: '',
      saving: false,
      fetching: false,
      searchQuery: '',
      categoryFilter: 'All',
      toastMessage: null,
      deleteConfirmId: null,
    };
    this.editorRef = React.createRef();
    this.onChange = this.onChange.bind(this);
    this.onSave = this.onSave.bind(this);
    this.fetchEntries = this.fetchEntries.bind(this);
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.confirmDelete = this.confirmDelete.bind(this);
    this.resetEditor = this.resetEditor.bind(this);
    this.handleToastClose = this.handleToastClose.bind(this);
  }

  async componentDidMount() {
    await this.fetchEntries();
    const spellCheckFalseElements = document.querySelectorAll('.editor-wrapper [spellcheck=false]');
    _.forEach(spellCheckFalseElements, (el) => {
      el.spellcheck = true;
    });
  }

  onChange(editorState) {
    this.setState({ editorState });
  }

  async fetchEntries() {
    this.setState({ fetching: true });
    try {
      const res = await axios.get('/admin/posts_v2');
      const items = _.map(res.data, (item) => ({
        ...item,
        impliedTitle: getImpliedTitle(item.json),
      }));
      this.setState({ entries: items, fetching: false });
    } catch (e) {
      console.error('Failed to fetch posts', e);
      this.setState({ fetching: false });
    }
  }

  handleEditClick(entry) {
    try {
      const parsedJson = JSON.parse(entry.json);
      const newEditorState = editorStateFromRaw(parsedJson);
      this.setState({
        editingPostId: entry.post_id,
        editingPostTitle: entry.impliedTitle,
        activeSelectTopic: entry.category || 'Jazz',
        editorState: newEditorState,
        toastMessage: `Loaded post "${entry.impliedTitle}" for editing.`,
      });
      if (this.editorRef.current) {
        this.editorRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (e) {
      console.error('Error loading post into editor', e);
      this.setState({ toastMessage: 'Failed to load post for editing.' });
    }
  }

  handleDeleteClick(postId) {
    this.setState({ deleteConfirmId: postId });
  }

  async confirmDelete() {
    const { deleteConfirmId, editingPostId } = this.state;
    if (!deleteConfirmId) return;
    try {
      await axios.delete(`/admin/post/${deleteConfirmId}`);
      this.setState({
        toastMessage: 'Post deleted successfully.',
        deleteConfirmId: null,
      });
      if (editingPostId === deleteConfirmId) {
        this.resetEditor();
      }
      this.fetchEntries();
    } catch (e) {
      console.error('Failed to delete post', e);
      this.setState({
        toastMessage: 'Failed to delete post.',
        deleteConfirmId: null,
      });
    }
  }

  resetEditor() {
    this.setState({
      editingPostId: null,
      editingPostTitle: '',
      editorState: editorStateFromRaw(null),
    });
  }

  async onSave() {
    const { editingPostId, activeSelectTopic, editorState } = this.state;
    this.setState({ saving: true });
    const jsonStr = editorStateToJSON(editorState);

    if (editingPostId) {
      try {
        await axios.post('/admin/post', {
          postId: editingPostId,
          json: jsonStr,
        });
        this.setState({
          saving: false,
          toastMessage: 'Blog post updated successfully!',
        });
        await this.fetchEntries();
      } catch (e) {
        console.error('Failed to update post', e);
        this.setState({
          saving: false,
          toastMessage: 'Error updating post. Please try again.',
        });
      }
    } else {
      try {
        await axios.put('/admin/post', {
          category: activeSelectTopic,
          json: jsonStr,
        });
        this.setState({
          saving: false,
          toastMessage: 'New blog post published successfully!',
        });
        this.resetEditor();
        await this.fetchEntries();
      } catch (e) {
        console.error('Failed to save post', e);
        this.setState({
          saving: false,
          toastMessage: 'Error publishing post. Please try again.',
        });
      }
    }
  }

  handleToastClose() {
    this.setState({ toastMessage: null });
  }

  render() {
    const {
      editorState,
      activeSelectTopic,
      entries,
      editingPostId,
      editingPostTitle,
      saving,
      fetching,
      searchQuery,
      categoryFilter,
      toastMessage,
      deleteConfirmId,
    } = this.state;

    const filteredEntries = _.filter(entries, (entry) => {
      const matchesSearch = (entry.impliedTitle || '').toLowerCase().includes(searchQuery.toLowerCase())
        || (entry.post_id || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'All' || entry.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });

    const { wordCount, charCount } = getEditorStats(editorState);

    return (
      <div className="blog-posts-management-container" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        {/* Posts Data Table Section */}
        <Paper square elevation={2} style={{ padding: '20px', marginBottom: '24px', borderRadius: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#1565c0' }}>Blog Posts Management ({entries.length})</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  this.resetEditor();
                  if (this.editorRef.current) {
                    this.editorRef.current.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                + Create New Post
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={this.fetchEntries}
                disabled={fetching}
              >
                {fetching ? 'Refreshing...' : 'Refresh List'}
              </Button>
            </div>
          </div>

          {/* Search and Category Filters */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <TextField
              label="Search posts..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => this.setState({ searchQuery: e.target.value })}
              style={{ minWidth: '240px', flex: 1 }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: '160px' }}>
              <InputLabel>Category Filter</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => this.setState({ categoryFilter: e.target.value })}
                label="Category Filter"
              >
                <MenuItem value="All">All Categories</MenuItem>
                <MenuItem value="Jazz">Jazz</MenuItem>
                <MenuItem value="Medicine">Medicine</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Data Table */}
          <TableContainer component={Paper} variant="outlined" style={{ overflowX: 'auto', width: '100%' }}>
            <Table style={{ minWidth: '600px' }}>
              <TableHead style={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>Title / Preview</TableCell>
                  <TableCell style={{ fontWeight: 'bold', width: '120px' }}>Category</TableCell>
                  <TableCell style={{ fontWeight: 'bold', width: '170px' }}>Date Created</TableCell>
                  <TableCell style={{ fontWeight: 'bold', width: '170px', textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                      {fetching ? 'Loading blog posts...' : 'No blog posts found matching criteria.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry) => {
                    const isCurrentlyEditing = editingPostId === entry.post_id;
                    return (
                      <TableRow key={entry.post_id} selected={isCurrentlyEditing}>
                        <TableCell style={{ wordBreak: 'break-word' }}>
                          <strong>{entry.impliedTitle}</strong>
                          {isCurrentlyEditing && (
                            <Chip
                              label="Currently Editing"
                              size="small"
                              color="primary"
                              style={{ marginLeft: '10px', height: '20px', fontSize: '10px' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.category || 'Jazz'}
                            size="small"
                            style={{
                              backgroundColor: entry.category === 'Medicine' ? '#e8f5e9' : '#e3f2fd',
                              color: entry.category === 'Medicine' ? '#2e7d32' : '#1565c0',
                              fontWeight: 'bold',
                            }}
                          />
                        </TableCell>
                        <TableCell style={{ fontSize: '13px', color: '#555' }}>
                          {entry.created_at_utc ? new Date(entry.created_at_utc).toLocaleString() : 'N/A'}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => this.handleEditClick(entry)}
                            style={{ marginRight: '6px' }}
                          >
                            ✏️ Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => this.handleDeleteClick(entry.post_id)}
                          >
                            🗑 Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Megadraft Rich Text Editor Section */}
        <div ref={this.editorRef}>
          <Paper square elevation={3} style={{ padding: '24px', borderRadius: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #eee' }}>
              <div>
                <h3 style={{ margin: 0, color: '#1565c0' }}>
                  {editingPostId ? `Editing Post: "${editingPostTitle}"` : 'Create New Blog Post'}
                </h3>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {editingPostId ? `Post ID: ${editingPostId}` : 'Compose rich formatted text, embed images or videos, and select a category.'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <FormControl variant="outlined" size="small" style={{ minWidth: '140px' }} disabled={Boolean(editingPostId)}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={activeSelectTopic}
                    onChange={(e) => this.setState({ activeSelectTopic: e.target.value })}
                    label="Category"
                  >
                    <MenuItem value="Jazz">Jazz</MenuItem>
                    <MenuItem value="Medicine">Medicine</MenuItem>
                  </Select>
                </FormControl>

                {editingPostId && (
                  <Button size="small" onClick={this.resetEditor}>
                    Cancel Editing
                  </Button>
                )}
              </div>
            </div>

            <div className="editor-wrapper" style={{ minHeight: '280px', margin: '15px 0' }}>
              <MegadraftEditor
                editorState={editorState}
                onChange={this.onChange}
                plugins={[customImagePlugin, youtubePlugin]}
              />
            </div>

            {/* Metrics & Save Controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
              <div style={{ fontSize: '13px', color: '#666', fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}>
                📊 <strong>{wordCount}</strong> words | <strong>{charCount}</strong> characters | ~<strong>{Math.ceil(wordCount / 200)}</strong> min read
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={this.onSave}
                  disabled={saving}
                >
                  {saving ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CircularProgress size={18} color="inherit" /> Saving...
                    </span>
                  ) : editingPostId ? 'Update Post' : 'Publish Post'}
                </Button>
              </div>
            </div>
          </Paper>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={Boolean(deleteConfirmId)} onClose={() => this.setState({ deleteConfirmId: null })}>
          <DialogTitle>Confirm Delete Post</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to permanently delete this blog post? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ deleteConfirmId: null })}>
              Cancel
            </Button>
            <Button onClick={this.confirmDelete} color="secondary" variant="contained">
              Delete Post
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Toast */}
        <Snackbar
          open={Boolean(toastMessage)}
          autoHideDuration={4000}
          onClose={this.handleToastClose}
          message={toastMessage}
        />
      </div>
    );
  }
}

export default Editor;
